import {FormField, NotificationType, SlidingPanel} from '../../../../kit/src';
import * as React from 'react';
import {Form, FormApi, Text} from '../../../../kit/src';

import {DataLoader, EmptyState, ErrorNotification, IconColumn, Page, Paginate, SearchBar} from '../../../shared/components';
import {Context} from '../../../shared/context';
import {Project} from '../../../shared/models';
import {services} from '../../../shared/services';
import {useQuery} from '../../../shared/hooks/query';
import {useListSort} from '../../../shared/hooks/use-list-sort';
import {FlexTopBar} from '../../../shared/components';

export function ProjectsList() {
    const formApiRef = React.useRef<FormApi | null>(null);
    const ctx = React.useContext(Context);
    const query = useQuery();
    const searchText = query.get('search') || '';
    const [page, setPage] = React.useState(0);

    type SortKey = 'name' | 'description';
    const {sortKey, requestSort, sortIcon, compareString} = useListSort<SortKey>('name');

    return (
        <Page
            title='Projects'
            toolbar={{
                breadcrumbs: [{title: 'Settings', path: '/settings'}, {title: 'Projects'}]
            }}>
            <FlexTopBar
                toolbar={{
                    breadcrumbs: [{title: 'Settings', path: '/settings'}, {title: 'Projects'}],
                    actionMenu: {
                        className: 'fa fa-plus',
                        items: [{title: 'New Project', iconClassName: 'fa fa-plus', action: () => ctx.navigation.goto('.', {add: true}, {replace: true})}]
                    },
                    tools: (
                        <SearchBar
                            value={searchText}
                            onChange={value => {
                                ctx.navigation.goto('.', {search: value || null}, {replace: true});
                                setPage(0);
                            }}
                            placeholder='Search projects...'
                        />
                    )
                }}
            />
            <div className='projects kit-container'>
                <DataLoader load={() => services.projects.list()}>
                    {projects => {
                        const filteredProjects = projects
                            .filter(
                                proj =>
                                    searchText === '' ||
                                    proj.metadata.name.toLowerCase().includes(searchText.toLowerCase()) ||
                                    (proj.spec.description && proj.spec.description.toLowerCase().includes(searchText.toLowerCase()))
                            )
                            .sort((a, b) => {
                                switch (sortKey) {
                                    case 'name':
                                        return compareString(a.metadata.name, b.metadata.name);
                                    case 'description':
                                        return compareString(a.spec.description, b.spec.description);
                                    default:
                                        return 0;
                                }
                            });

                        return (
                            <>
                                {filteredProjects.length > 0 ? (
                                    <Paginate page={page} data={filteredProjects} onPageChange={setPage} preferencesKey='projects-list'>
                                        {projectsToDisplay => (
                                            <div className='kit-table-list kit-table-list--clickable'>
                                                <div className='kit-table-list__head'>
                                                    <div className='row'>
                                                        <IconColumn />
                                                        <div className='columns small-3 sortable' onClick={() => requestSort('name')}>
                                                            Name
                                                            {sortIcon('name')}
                                                        </div>
                                                        <div className='columns small-6 sortable' onClick={() => requestSort('description')}>
                                                            Description
                                                            {sortIcon('description')}
                                                        </div>
                                                    </div>
                                                </div>
                                                {projectsToDisplay.map(proj => (
                                                    <div className='kit-table-list__row' key={proj.metadata.name} onClick={() => ctx.navigation.goto(`./${proj.metadata.name}`)}>
                                                        <div className='row'>
                                                            <IconColumn icon='fa fa-object-group' />
                                                            <div className='columns small-3'>{proj.metadata.name}</div>
                                                            <div className='columns small-6'>{proj.spec.description}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Paginate>
                                ) : projects.length === 0 ? (
                                    <EmptyState icon='fa fa-object-group'>
                                        <h4>No projects yet</h4>
                                        <h5>Create new projects to group your applications</h5>
                                        <button className='kit-button kit-button--base' onClick={() => ctx.navigation.goto('.', {add: true}, {replace: true})}>
                                            New project
                                        </button>
                                    </EmptyState>
                                ) : (
                                    <EmptyState icon='fa fa-object-group'>
                                        <h4>No projects matched your search</h4>
                                        <h5>Try adjusting your search query</h5>
                                    </EmptyState>
                                )}
                            </>
                        );
                    }}
                </DataLoader>
            </div>
            <SlidingPanel
                isShown={query.get('add') === 'true'}
                onClose={() => ctx.navigation.goto('.', {add: null}, {replace: true})}
                isMiddle={true}
                header={
                    <div>
                        <button onClick={() => formApiRef.current && formApiRef.current.submitForm(null)} className='kit-button kit-button--base'>
                            Create
                        </button>{' '}
                        <button onClick={() => ctx.navigation.goto('.', {add: null}, {replace: true})} className='kit-button kit-button--base-o'>
                            Cancel
                        </button>
                    </div>
                }>
                <Form
                    defaultValues={{metadata: {}, spec: {}}}
                    getApi={api => (formApiRef.current = api)}
                    validateError={(p: Project) => ({
                        'metadata.name': !p.metadata.name && 'Project Name is required'
                    })}
                    onSubmit={async (proj: Project) => {
                        try {
                            await services.projects.create(proj);
                            ctx.navigation.goto(`./${proj.metadata.name}`, {add: null}, {replace: true});
                        } catch (e) {
                            ctx.notifications.show({
                                content: <ErrorNotification title='Unable to create project' e={e} />,
                                type: NotificationType.Error
                            });
                        }
                    }}>
                    {api => (
                        <form onSubmit={api.submitForm} role='form' className='width-control'>
                            <div className='white-box'>
                                <p>General</p>
                                <div className='kit-form-row'>
                                    <FormField formApi={api} label='Project Name' field='metadata.name' component={Text} />
                                </div>
                                <div className='kit-form-row'>
                                    <FormField formApi={api} label='Description' field='spec.description' component={Text} />
                                </div>
                            </div>
                        </form>
                    )}
                </Form>
            </SlidingPanel>
        </Page>
    );
}
